
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { 
  Search, 
  Filter, 
  Download, 
  Plus, 
  QrCode, 
  Calendar, 
  Mail, 
  RefreshCcw 
} from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/components/ui/use-toast";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getAllVoucherTypes, VoucherType } from "@/lib/db/voucherDb";

const AssignVoucherTab: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [voucherTypes, setVoucherTypes] = useState<VoucherType[]>([]);
  const { toast } = useToast();
  
  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm({
    defaultValues: {
      voucherType: "",
      customerName: "",
      icNumber: "",
      mobileNumber: "",
      memberType: "Customer",
      voucherAmount: 0,
      expiryDate: new Date(),
      notes: ""
    }
  });
  
  useEffect(() => {
    const loadVoucherTypes = async () => {
      setIsLoading(true);
      try {
        const types = await getAllVoucherTypes();
        setVoucherTypes(types);
        setIsLoading(false);
      } catch (error) {
        console.error("Error loading voucher types:", error);
        toast({
          variant: "destructive",
          title: "Failed to load voucher types",
          description: "Could not retrieve voucher types. Please try again."
        });
        setIsLoading(false);
      }
    };
    
    loadVoucherTypes();
  }, [toast]);
  
  const onSubmit = async (data: any) => {
    console.log("Form Data:", data);
    toast({
      title: "Voucher Assigned",
      description: "A new voucher has been assigned successfully."
    });
  };

  return (
    <div className="space-y-6">
      {/* Assign Voucher Form */}
      <Card>
        <CardHeader>
          <CardTitle>Assign New Voucher</CardTitle>
          <CardDescription>
            Fill in the details to assign a new voucher to a customer.
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="voucherType">Voucher Type</Label>
                <Select onValueChange={(value) => setValue("voucherType", value)}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a voucher type" />
                  </SelectTrigger>
                  <SelectContent>
                    {voucherTypes.map((type) => (
                      <SelectItem key={type.typeId} value={type.typeId}>{type.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="voucherAmount">Voucher Amount (RM)</Label>
                <Input
                  id="voucherAmount"
                  type="number"
                  min="1"
                  step="1"
                  placeholder="Enter amount"
                  {...register("voucherAmount", { valueAsNumber: true })}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="customerName">Customer Name</Label>
                <Input
                  id="customerName"
                  placeholder="Enter customer name"
                  {...register("customerName")}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="icNumber">IC Number</Label>
                <Input
                  id="icNumber"
                  placeholder="Enter IC number"
                  {...register("icNumber")}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="mobileNumber">Mobile Number</Label>
                <Input
                  id="mobileNumber"
                  placeholder="Enter mobile number"
                  {...register("mobileNumber")}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="memberType">Member Type</Label>
                <Select onValueChange={(value) => setValue("memberType", value)}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select member type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Customer">Customer</SelectItem>
                    <SelectItem value="Coop Member">Coop Member</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="expiryDate">Expiry Date</Label>
              <Input
                id="expiryDate"
                type="date"
                {...register("expiryDate", { valueAsDate: true })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                placeholder="Additional notes or comments"
                className="min-h-[80px]"
                {...register("notes")}
              />
            </div>
          </CardContent>
          
          <CardFooter className="flex justify-end">
            <Button type="submit">
              Assign Voucher
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default AssignVoucherTab;
